import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from '../api/axiosInstance';

export const useSubscription = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [featureUsage, setFeatureUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // Get authentication state
  const { user, accessToken } = useSelector((state) => state.auth);

  // Fetch subscription status
  const fetchSubscriptionStatus = useCallback(async () => {
    // Don't fetch if user is not authenticated
    if (!user || !accessToken) {
      setLoading(false);
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/payments/subscription-status/');
      console.log('Frontend - Subscription status response:', response.data);
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      if (err.response?.status === 401) {
        setError('Please log in to view subscription information');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch subscription status');
      }
    } finally {
      setLoading(false);
    }
  }, [user, accessToken]);

  // Check feature access
  const checkFeatureAccess = useCallback((featureName) => {
    if (!subscriptionStatus) return false;
    
    // Handle different user roles
    const userRole = user?.role;
    
    if (userRole === 'receptionist') {
      // Receptionist-specific feature mapping
      const receptionistFeatures = {
        'appointment_management': 'appointment_management',
        'patient_registration': 'patient_registration', 
        'billing_management': 'billing_management',
        'receptionist_features': 'receptionist_features',
        'video_consultations': 'video_consultations',
        'advanced_analytics': 'advanced_analytics',
        'bulk_operations': 'bulk_operations',
        'api_access': 'api_access',
        'custom_branding': 'custom_branding',
      };
      
      const mappedFeature = receptionistFeatures[featureName] || featureName;
      return subscriptionStatus.can_access_features?.[mappedFeature] || false;
    }
    
    if (userRole === 'pharmacist') {
      // Pharmacist-specific feature mapping
      const pharmacistFeatures = {
        'prescription_management': 'prescription_management',
        'inventory_management': 'inventory_management',
        'pharmacist_features': 'pharmacist_features',
        'advanced_analytics': 'advanced_analytics',
      };
      
      const mappedFeature = pharmacistFeatures[featureName] || featureName;
      return subscriptionStatus.can_access_features?.[mappedFeature] || false;
    }
    
    if (userRole === 'doctor') {
      // Doctor features - direct mapping
      return subscriptionStatus.can_access_features?.[featureName] || false;
    }
    
    // Default for other roles (patient, caregiver, etc.)
    return subscriptionStatus.can_access_features?.basic_features || false;
  }, [subscriptionStatus, user?.role]);

  // Get subscription info
  const getSubscriptionInfo = useCallback(() => {
    if (!subscriptionStatus) return null;
    
    const userRole = user?.role;
    const status = subscriptionStatus.status;
    
    return {
      type: status?.type || 'unknown',
      status: status?.status || 'unknown',
      isActive: status?.is_active || false,
      isTrial: status?.is_trial || false,
      planName: status?.plan_name || 'No Plan',
      planType: status?.plan || 'none',
      daysRemaining: status?.days_remaining || 0,
      userRole: userRole,
      clinicName: status?.clinic_name || null,
      clinicId: status?.clinic_id || null,
      message: status?.message || null,
    };
  }, [subscriptionStatus, user?.role]);

  // Get usage information
  const getUsageInfo = useCallback((featureName) => {
    if (!subscriptionStatus?.usage) return null;
    
    return subscriptionStatus.usage[featureName] || {
      current_usage: 0,
      limit: -1,
      remaining: -1,
      has_access: false,
      is_limit_reached: false
    };
  }, [subscriptionStatus]);

  // Refresh subscription status
  const refreshSubscription = useCallback(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Initialize subscription status
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Check if user is authenticated
  const isAuthenticated = !!(user && accessToken);

  // Get subscription status methods
  const hasActiveSubscription = useCallback(() => {
    if (!subscriptionStatus?.status) return false;
    return subscriptionStatus.status.is_active || subscriptionStatus.status.is_trial;
  }, [subscriptionStatus]);

  const isOnTrial = useCallback(() => {
    if (!subscriptionStatus?.status) return false;
    return subscriptionStatus.status.is_trial;
  }, [subscriptionStatus]);

  const getPlanType = useCallback(() => {
    if (!subscriptionStatus?.status) return 'none';
    return subscriptionStatus.status.plan || 'none';
  }, [subscriptionStatus]);

  const getCurrentUsage = useCallback((featureName) => {
    if (!subscriptionStatus?.usage) return 0;
    return subscriptionStatus.usage[featureName]?.current_usage || 0;
  }, [subscriptionStatus]);

  const getDaysRemaining = useCallback(() => {
    if (!subscriptionStatus?.status) return 0;
    return subscriptionStatus.status.days_remaining || 0;
  }, [subscriptionStatus]);

  const getUsagePercentage = useCallback((featureName) => {
    if (!subscriptionStatus?.usage) return 0;
    const usage = subscriptionStatus.usage[featureName];
    if (!usage || usage.is_unlimited || usage.limit === 0) return 0;
    return Math.round((usage.current_usage / usage.limit) * 100);
  }, [subscriptionStatus]);

  const isUsageLimitReached = useCallback((featureName) => {
    if (!subscriptionStatus?.usage) return false;
    const usage = subscriptionStatus.usage[featureName];
    return usage && !usage.is_unlimited && usage.remaining <= 0;
  }, [subscriptionStatus]);

  const isUsageLimitNear = useCallback((featureName, threshold = 0.8) => {
    if (!subscriptionStatus?.usage) return false;
    const usage = subscriptionStatus.usage[featureName];
    if (!usage || usage.is_unlimited) return false;
    return (usage.current_usage / usage.limit) >= threshold;
  }, [subscriptionStatus]);

  const canAccessFeature = useCallback((featureName) => {
    if (!subscriptionStatus) return false;
    
    const featureAccess = subscriptionStatus.can_access_features?.[featureName];
    if (featureAccess !== undefined) {
      return featureAccess;
    }
    
    const usage = subscriptionStatus.usage?.[featureName];
    return usage?.has_access || false;
  }, [subscriptionStatus]);

  const getUpgradeRecommendations = useCallback(() => {
    return subscriptionStatus?.recommendations || [];
  }, [subscriptionStatus]);

  const refresh = useCallback(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return {
    subscriptionStatus,
    featureUsage,
    loading,
    error,
    isAuthenticated,
    hasActiveSubscription,
    isOnTrial,
    getPlanType,
    getCurrentUsage,
    getDaysRemaining,
    getUsagePercentage,
    isUsageLimitReached,
    isUsageLimitNear,
    canAccessFeature,
    getUpgradeRecommendations,
    refresh,
    checkFeatureAccess,
    getSubscriptionInfo,
    getUsageInfo,
    refreshSubscription,
    fetchSubscriptionStatus,
  };
}; 